import { Suspense, lazy } from 'react';
import { RouteObject } from 'react-router';

import Main from 'layouts/Main';
import { AccessElements, accessElementsArray } from 'helpers/app.config';
import StatusFlow from 'pages/Admin/ProblemManager/StatusFlows';

import AuthPage from './AuthPage';
import LoadingFallback from './LoadingFallback';

/** Página principal para la creación y edición de tickets */
const CrudTicketPage = lazy(() => import('pages/Ticket/Crud'));
/** Página de servicio de tickets (CRUD) */
const CrudTicketServicePage = lazy(() => import('pages/Ticket/Crud'));
/** Página principal para la creación y edición de cambios */
const CrudChangePage = lazy(() => import('pages/Changes/Crud/CrudChange'));
/** Página para crear ODT de tickets */
const TicketODTCreate = lazy(() => import('pages/Ticket/ODT/Create'));
/** Página para buscar ODT de tickets */
const TicketODTSearch = lazy(() => import('pages/Ticket/ODT/Search'));
/** Pestañas de ODT de tickets */
const TicketODTTabs = lazy(() => import('pages/Ticket/ODT'));
/** Página principal de búsqueda de tickets */
const FindTicketPage = lazy(() => import('pages/Ticket/Find'));
/** Dashboard principal */
const DashboardPage = lazy(() => import('pages/Dashboard'));
/** Página de acceso prohibido */
const ForbiddenPage = lazy(() => import('pages/Forbidden'));
/** Página de no encontrado (404) */
const NotFoundPage = lazy(() => import('pages/NotFound'));
/** Detalle de ticket */
const TicketDetail = lazy(() => import('pages/Ticket/Detail'));
/** Página de búsqueda de almacenes */
const FindWarehouse = lazy(() => import('pages/Warehouse/Find'));
/** Reporte de envíos de almacén */
const ShippingReport = lazy(() => import('pages/Warehouse/ShippingReport'));
/** CRUD de registros de almacén */
const CrudShippingRecord = lazy(() => import('pages/Warehouse/Crud'));

const ProjectsList = lazy(() => import('pages/Admin/ProjectsList'));

const Ubications = lazy(() => import('pages/Admin/Ubications/Ubication'));
const UbicationsMap = lazy(
  () => import('pages/Admin/Ubications/UbicationsMap')
);
const MassiveDeactivateUbications = lazy(
  () => import('pages/Admin/Ubications/MassiveDeactivate')
);

const MassiveLoadUbications = lazy(
  () => import('pages/Admin/Ubications/MassiveLoadUbications')
);

const ConfigComponentsMap = lazy(
  () => import('pages/ConfigurationComponent/ConfigComponetMap')
);

const UbicationsWorkForce = lazy(
  () => import('pages/Admin/Ubications/WorkForce')
);

const PermissionsAudit = lazy(() => import('pages/Admin/PermissionsAudit'));

/* ADMIN CATALOGS */

const FrecuentlyFailure = lazy(
  () => import('pages/Admin/Cataloges/FreqFailure')
);
const ClientsManagment = lazy(() => import('pages/Admin/Clients'));

const ServiceType = lazy(() => import('pages/Admin/Cataloges/ServiceType'));
const UserConfig = lazy(() => import('pages/Admin/Users'));
const UbicationPriority = lazy(
  () => import('pages/Admin/Cataloges/UbicationPriority')
);
const Brand = lazy(() => import('pages/Admin/Cataloges/Brand'));
const Model = lazy(() => import('pages/Admin/Cataloges/Model'));
const Reasons = lazy(() => import('pages/Admin/Cataloges/Reasons'));

const Directions = lazy(() => import('pages/Admin/Cataloges/Directions'));
const MassiveLoadExternalFile = lazy(
  () => import('pages/Ticket/MassiveLoad/ByExternalFile')
);
const CommentTypes = lazy(() => import('pages/Admin/Cataloges/CommentType'));
const UserProfile = lazy(() => import('pages/Admin/Cataloges/UserProfiles'));
const CABManagement = lazy(
  () => import('pages/Admin/ChangeManagement/CABManagement')
);
const AreaCatalogue = lazy(() => import('pages/Admin/Cataloges/Area'));
const ClasificationTicket = lazy(
  () => import('pages/Admin/Cataloges/ClasificationTicket')
);

const InvoiceTAGCatalogue = lazy(
  () => import('pages/Admin/Cataloges/InvoiceTAG')
);

const InvoiceFuelCatalogue = lazy(
  () => import('pages/Admin/Cataloges/InvoiceFuel')
);

const InvoiceExpenseCodes = lazy(
  () => import('pages/Admin/Cataloges/InvoiceExpenseCode')
);
const StatusCongurationComponentsCatalogue = lazy(
  () => import('pages/Admin/Cataloges/StatusConfigurationComponts')
);
const ServiceClassCatalogue = lazy(
  () => import('pages/Admin/Cataloges/ServiceClass')
);
const RegionCatalogue = lazy(() => import('pages/Admin/Cataloges/Region'));
const StateCatalogue = lazy(() => import('pages/Admin/Cataloges/States'));
const ProjectsCatalogue = lazy(() => import('pages/Admin/Cataloges/Projects'));
const WorkFlowType = lazy(() => import('pages/Admin/Cataloges/WorkFlowType'));
const ComponentCondition = lazy(
  () => import('pages/Admin/Cataloges/ComponentCondition')
);
const CurrencyCatalogue = lazy(() => import('pages/Admin/Cataloges/Currency'));
const Audit = lazy(() => import('pages/Admin/Audit'));
const AuditCatalog = lazy(() => import('pages/Admin/Cataloges/AuditCatalog'));
const KPIs = lazy(() => import('pages/Admin/KPIs'));
const FormsManagement = lazy(() => import('pages/Admin/FormDinamic'));
const AccessLogbook = lazy(() => import('pages/Admin/AccessLogbook'));
const StatusCatalogue = lazy(() => import('pages/Admin/Cataloges/Status'));
const FlowDashboard = lazy(() => import('pages/Admin/Cataloges/FlowBoard'));

const IncidetAndRequirmentFlow = lazy(
  () => import('pages/Admin/SystemAdminManagment')
);
const ResponsibleType = lazy(
  () => import('pages/Admin/Cataloges/ResponsibleType')
);
const Calendar = lazy(() => import('pages/Admin/Cataloges/Calendar'));

const ConfigurationComponentCatalog = lazy(
  () => import('pages/ConfigurationComponent/ConfigurationComponentCatalog')
);

const MassiveLoadProduct = lazy(
  () => import('pages/ConfigurationComponent/MassiveLoadConfigurationComponent')
);

const MassiveUpdateProduct = lazy(
  () => import('pages/ConfigurationComponent/MassiveUpdate')
);

const Supplies = lazy(() => import('pages/Admin/Cataloges/Supplies'));
const StockSupplies = lazy(() => import('pages/StockSupplies'));

const StoreCatalog = lazy(() => import('pages/Store/StoreCatalog'));

const LaboratoryComponentInventory = lazy(
  () =>
    import('pages/AssetManagementIT/Laboratory/LaboratoryComponentInventory')
);

const LaboratoryBulkCIIntake = lazy(
  () => import('pages/AssetManagementIT/Laboratory/BulkCIIntake')
);

const AuditReport = lazy(() => import('pages/AssetManagementIT/AuditReport'));
const WarehousesComponentInventory = lazy(
  () =>
    import(
      'pages/AssetManagementIT/WarehouseInventory/WarehousesComponentInventory'
    )
);
const WarehousesSupplyInventory = lazy(
  () =>
    import(
      'pages/AssetManagementIT/WarehouseInventory/WarehousesSupplyInventory'
    )
);
const KitsInventoryWarehouse = lazy(
  () =>
    import('pages/AssetManagementIT/WarehouseInventory/KitsInventoryWarehouse')
);
const WorkForceConfigComponentsInventory = lazy(
  () => import('pages/AssetManagementIT/WorkForce/ConfigComponents')
);

const WorkForceSuppliesInventory = lazy(
  () => import('pages/AssetManagementIT/WorkForce/Supplies')
);

const InterventionCodes = lazy(
  () => import('pages/Admin/Cataloges/InterventionCodes')
);

const Category = lazy(() => import('pages/Admin/Cataloges/Category'));
const ExpertCommitteesCatalog = lazy(
  () => import('pages/Admin/ProblemManager/ExpertCommitteesCatalog')
);
const ProblemCausesCatalog = lazy(
  () => import('pages/Admin/ProblemManager/CausesCatalog')
);

const Impact = lazy(() => import('pages/Admin/Cataloges/Impact'));

const Urgency = lazy(() => import('pages/Admin/Cataloges/Urgency'));

const Priority = lazy(() => import('pages/Admin/Cataloges/Priority'));
const PrioritySurveysSearch = lazy(
  () => import('pages/Admin/Cataloges/PrioritySurveys')
);

const CriticalityTicket = lazy(
  () => import('pages/Admin/Cataloges/CriticalityTicket')
);

const WorkForceKitCIInventory = lazy(
  () => import('pages/AssetManagementIT/WorkForce/KitsCI')
);

const Indicators = lazy(() => import('pages/Admin/Indicators'));
const KitsCI = lazy(() => import('pages/Kits'));

const PostalCodesCatalogue = lazy(
  () => import('pages/Admin/Cataloges/PostalCode')
);

const EquipmentTypeCatalog = lazy(
  () => import('pages/Admin/Cataloges/EquipmentTypeCatalog')
);

const InformativeModuleWorkForce = lazy(
  () => import('pages/InformativeModule/WorkForce')
);

const InformativeModuleClients = lazy(
  () => import('pages/InformativeModule/Client')
);

const UnitsMeasurementCatalogue = lazy(
  () => import('pages/Admin/Cataloges/UnitsMeasurement')
);

const WarehouseType = lazy(
  () => import('pages/Admin/Cataloges/WarehouseTypeCatalogue')
);

const NotificationsPage = lazy(() => import('pages/Notifications'));

const FAQs = lazy(() => import('pages/Help/FAQs'));

const Reports = lazy(() => import('pages/Reports/LinkReport'));
const IncidentOpenReport = lazy(() => import('pages/Reports/incidents/open'));
const IncidentHistoricalReport = lazy(
  () => import('pages/Reports/incidents/historical')
);

const ReportsCatalogue = lazy(
  () => import('pages/Admin/Cataloges/ReportCatalogue')
);

const ExportTicketReports = lazy(() => import('pages/ExportTicketReports'));

const RecidivistsReports = lazy(
  () => import('pages/Reports/RecidivistsReports')
);
const ExpenseReports = lazy(() => import('pages/Reports/ExpenseReport'));
const MayorIncident = lazy(() => import('pages/Reports/MayorIncidentReport'));
const KnowledgeManagementDatabase = lazy(() => import('pages/KMDB'));
const RemoteWork = lazy(() => import('pages/Reports/RemoteWork'));
const Surveys = lazy(() => import('pages/Surveys/NewSurvey'));
const NotificationCatalogue = lazy(
  () => import('pages/Admin/Cataloges/Notification')
);
const NotificationConfigurator = lazy(
  () => import('pages/Admin/NotificationConfigurator')
);
const NotificationConfiguratorForm = lazy(
  () => import('pages/Admin/NotificationConfigurator/Form')
);
const SurveySearch = lazy(() => import('pages/Surveys/SurveySearch'));
const SurveyReport = lazy(() => import('pages/Surveys/SurveyReport'));

const Groups = lazy(() => import('pages/Admin/Groups'));

const TowerModel = lazy(() => import('pages/Admin/Cataloges/TowerModel'));
const TowerType = lazy(() => import('pages/Admin/Cataloges/TowerType'));
const TowerProperty = lazy(() => import('pages/Admin/Cataloges/TowerProperty'));
const HierarchyLevels = lazy(
  () => import('pages/Admin/Cataloges/HierarchyLevels')
);
const HierarchicalStructure = lazy(
  () => import('pages/Admin/HierarchicalStructure')
);
const MassiveHierarchicalStructureUpdate = lazy(
  () => import('pages/Admin/Users/MassiveHierarchicalStructureUpdate')
);

const StatesOfTasks = lazy(
  () => import('pages/Admin/TaskManagment/TaskStatus')
);
const TasksTemplate = lazy(
  () => import('pages/Admin/TaskManagment/TaskTemplate')
);
// chatbot
// const Chatbot = lazy(() => import('pages/Chatbot'));

// SE COMENTA PARA LA SALIDA A QA 19/06/25

// problemas
const ProblemSearch = lazy(() => import('pages/Problems/Search'));
const NewProblem = lazy(() => import('pages/Problems/Create'));
const RedirectProblemEditToDetail = lazy(
  () => import('pages/Problems/Edit/RedirectToDetail')
);
const ProblemDetails = lazy(() => import('pages/Problems/Details'));
const ProblemTemplates = lazy(
  () => import('pages/Admin/ProblemManager/ProblemTemplates')
);
const CreateEditProblemTemplate = lazy(
  () => import('pages/Admin/ProblemManager/ProblemTemplates/CreateEdit')
);

const FindSolutions = lazy(() => import('pages/Solutions/Find'));
const CreateNewSolution = lazy(() => import('pages/Solutions/Create'));
const SolutionDetail = lazy(() => import('pages/Solutions/Detail'));

const SurveyTemplate = lazy(
  () => import('pages/Surveys/components/SurveyTemplate')
);

// cambios
const ChangeTypes = lazy(
  () => import('pages/Admin/ChangeManagement/ChangeTypes')
);
const StatesOfChange = lazy(
  () => import('pages/Admin/ChangeManagement/StageOfChanges')
);
const StatesAndStagesOfChanges = lazy(
  () => import('pages/Admin/ChangeManagement/StatesAndStagesOfChanges')
);
const FlowOfChanges = lazy(
  () => import('pages/Admin/ChangeManagement/FlowOfChanges')
);
const ChangeDetail = lazy(() => import('pages/Changes/Detail'));
const SearchChange = lazy(() => import('pages/Changes/Search'));
const ChangeCalendar = lazy(() => import('pages/Changes/Calendar'));
// lanzamientos
const CreateRelease = lazy(() => import('pages/Releases/Create'));
const SearchRelease = lazy(() => import('pages/Releases/Search'));
const ReleaseDetail = lazy(() => import('pages/Releases/Details'));
const ReleaseUpdate = lazy(() => import('pages/Releases/Edit'));
// soluciones
const SolutionTopics = lazy(
  () => import('pages/Admin/Cataloges/SolutionTopics')
);
const SolutionType = lazy(() => import('pages/Admin/Cataloges/SolutionType'));

const Status = lazy(() => import('pages/Admin/ProblemManager/Status'));
const StatusRelease = lazy(() => import('pages/Admin/ReleaseManager/Status'));
const StatusFlowRelease = lazy(
  () => import('pages/Admin/ReleaseManager/StatusFlows')
);

const AutomaticProblemDetection = lazy(
  () => import('pages/Admin/ProblemManager/AutomaticProblemDetection')
);

// TODO: Descomentar cuando se haga template de lanzamientos
// const ReleaseTemplates = lazy(
//   () => import('pages/Admin/ReleaseManager/ReleaseTemplates')
// );

const routes: RouteObject[] = [
  {
    path: `/`,
    element: <AuthPage element={<Main />} />,
    children: [
      {
        path: '/',
        element: (
          <AuthPage
            element={<DashboardPage />}
            permissions={accessElementsArray}
          />
        )
      },
      {
        path: 'release/find',
        element: (
          <AuthPage
            element={<SearchRelease />}
            permissions={[AccessElements.ReleaseSearch]}
          />
        )
      },
      {
        path: 'release/detail/:id',
        element: (
          <AuthPage
            element={<ReleaseDetail />}
            permissions={[AccessElements.ReleaseGetById]}
          />
        )
      },
      {
        path: 'release/update/:id',
        element: (
          <AuthPage
            element={<ReleaseUpdate />}
            permissions={[AccessElements.ReleaseUpdate]}
          />
        )
      },
      {
        path: 'requirement/find',
        element: (
          <AuthPage
            element={<FindTicketPage />}
            permissions={[
              AccessElements.TicketSearchGeneral,
              AccessElements.TicketCreate,
              AccessElements.TicketAcceptAssignation,
              AccessElements.TicketRejectAssignation,
              AccessElements.TicketUpdateToCanceledStatus,
              AccessElements.TicketUpdateToClosedStatus,
              AccessElements.TicketUpdateToTravelStatus,
              AccessElements.TicketUpdateToOnSiteStatus,
              AccessElements.TicketUpdateToQuickRecoverStatus,
              AccessElements.TicketUpdateToHistoricStatus,
              AccessElements.TicketUpdateToPendingValidationStatus,
              AccessElements.TicketUpdateToPendingReasonStatus
            ]}
          />
        )
      },
      {
        path: 'requirement/createTicket/incident',
        element: (
          <AuthPage
            element={<CrudTicketPage />}
            permissions={[AccessElements.TicketCreate]}
          />
        )
      },
      {
        path: 'requirement/createTicket/service',
        element: (
          <AuthPage
            element={<CrudTicketServicePage />}
            permissions={[AccessElements.TicketCreate]}
          />
        )
      },
      {
        path: 'change/create',
        element: (
          <AuthPage
            element={<CrudChangePage />}
            permissions={[AccessElements.ChangeCreate]}
          />
        )
      },
      {
        path: 'change/find',
        element: (
          <AuthPage
            element={<SearchChange />}
            permissions={[AccessElements.ChangeSearch]}
          />
        )
      },
      {
        path: 'change/calendar',
        element: (
          <AuthPage
            element={<ChangeCalendar />}
            permissions={[
              AccessElements.ChangeCalendarView,
              AccessElements.ChangeSearch
            ]}
          />
        )
      },
      {
        path: 'requirement/odt/create',
        element: (
          <AuthPage
            element={<TicketODTCreate />}
            permissions={[AccessElements.TicketCreateODT]}
          />
        )
      },
      {
        path: 'requirement/odt/search',
        element: (
          <AuthPage
            element={<TicketODTSearch />}
            permissions={[
              AccessElements.TicketSearchODT,
              AccessElements.TicketCreateODT,
              AccessElements.TicketCancelODT,
              AccessElements.TicketQueryODTDetail
            ]}
          />
        )
      },
      {
        path: 'requirement/odt/:id',
        element: (
          <AuthPage
            element={<TicketODTTabs />}
            permissions={[AccessElements.TicketQueryODTDetail]}
          />
        )
      },
      {
        path: '/solutions/find',
        element: <AuthPage element={<FindSolutions />} permissions={[]} />
      },
      {
        path: '/solutions/create',
        element: <AuthPage element={<CreateNewSolution />} permissions={[]} />
      },
      {
        path: '/solutions/detail/:solutionId',
        element: <AuthPage element={<SolutionDetail />} permissions={[]} />
      },
      // {
      //   path: '/chatbot',
      //   element: (
      //     <AuthPage
      //       element={<Chatbot />}
      //       permissions={[AccessElements.TicketQueryODTDetail]}
      //     />
      //   )
      // },
      {
        path: 'requirement/detail/:ticketId/:tab',
        element: (
          <AuthPage
            element={<TicketDetail />}
            permissions={[AccessElements.TicketGetById]}
          />
        )
      },
      {
        path: 'change/detail/:changeId/:tab',
        element: (
          <AuthPage
            element={<ChangeDetail />}
            permissions={[
              AccessElements.ChangeSearch,
              AccessElements.ChangeUpdate,
              AccessElements.ChangeCreate
            ]}
          />
        )
      },
      {
        path: 'shipping/find',
        element: (
          <AuthPage
            element={<FindWarehouse />}
            permissions={[
              AccessElements.ShipmentSearchGeneral,
              AccessElements.ShipmentCreate,
              AccessElements.ShipmentUpdate,
              AccessElements.AssetsStateChange,
              AccessElements.ShipmentDelete
            ]}
          />
        )
      },
      {
        path: 'shipping/report',
        element: <AuthPage element={<ShippingReport />} permissions={[]} />
      },
      {
        path: 'shipping/create',
        element: (
          <AuthPage
            element={<CrudShippingRecord />}
            permissions={[AccessElements.ShipmentCreate]}
          />
        )
      },
      {
        path: 'shipping/detail/:id',
        element: <AuthPage element={<CrudShippingRecord />} />
      },
      {
        path: 'shipping/reception/detail/:id',
        element: <AuthPage element={<CrudShippingRecord />} />
      },
      {
        path: '/configurationComponent',
        element: (
          <AuthPage
            element={<ConfigurationComponentCatalog />}
            permissions={[AccessElements.ProductSearchGeneral]}
          />
        )
      },
      {
        path: 'statusCongurationComponents',
        element: (
          <AuthPage
            element={<StatusCongurationComponentsCatalogue />}
            permissions={[
              AccessElements.ComponentStatusSearch,
              AccessElements.ComponentStatusAdd
            ]}
          />
        )
      },
      {
        path: 'configurationComponent/massiveLoad',
        element: (
          <AuthPage
            element={<MassiveLoadProduct />}
            permissions={[AccessElements.ProductBulkLoad]}
          />
        )
      },
      {
        path: 'configurationComponent/massiveUpdate',
        element: (
          <AuthPage
            element={<MassiveUpdateProduct />}
            permissions={[AccessElements.ProductBulkLoad]}
          />
        )
      },
      {
        path: 'admin/category',
        element: (
          <AuthPage
            element={<Category />}
            permissions={[
              AccessElements.CategoryCreate,
              AccessElements.CategorySearch
            ]}
          />
        )
      },
      {
        path: 'admin/solutionTopics',
        element: <AuthPage element={<SolutionTopics />} permissions={[]} />
      },
      {
        path: 'admin/solutionType',
        element: (
          <AuthPage
            element={<SolutionType />}
            permissions={[AccessElements.SolutionTypeSearch]}
          />
        )
      },
      {
        path: 'admin/impact',
        element: (
          <AuthPage
            element={<Impact />}
            permissions={[
              AccessElements.ImpactCreate,
              AccessElements.ImpactSearch
            ]}
          />
        )
      },
      {
        path: 'admin/priority',
        element: (
          <AuthPage
            element={<Priority />}
            permissions={[
              AccessElements.TicketPriorityCreate,
              AccessElements.TicketPrioritySearch
            ]}
          />
        )
      },
      {
        path: 'admin/prioritySurveys',
        element: (
          <AuthPage
            element={<PrioritySurveysSearch />}
            permissions={[AccessElements.SurveySearch]}
          />
        )
      },
      {
        path: 'admin/prioritySurveys/create',
        element: (
          <AuthPage
            element={<SurveyTemplate isANewTemplate />}
            permissions={[AccessElements.SurveyManagement]}
          />
        )
      },
      {
        path: 'admin/prioritySurveys/edit/:id',
        element: (
          <AuthPage
            element={<SurveyTemplate isANewTemplate isEditableView />}
            permissions={[AccessElements.SurveyUpdate]}
          />
        )
      },
      {
        path: 'admin/urgency',
        element: (
          <AuthPage
            element={<Urgency />}
            permissions={[
              AccessElements.UrgencyCreate,
              AccessElements.UrgencySearch
            ]}
          />
        )
      },
      {
        path: 'admin/create/criticality',
        element: (
          <AuthPage
            element={<CriticalityTicket />}
            permissions={[
              AccessElements.AssignmentPriorityCreate,
              AccessElements.AssignmentPrioritySearch
            ]}
          />
        )
      },
      {
        path: 'admin/stores',
        element: (
          <AuthPage
            element={<StoreCatalog />}
            permissions={[
              AccessElements.WarehouseCreate,
              AccessElements.WarehouseSearchGeneral
            ]}
          />
        )
      },
      {
        path: 'admin/project',
        element: <AuthPage element={<ProjectsList />} />
      },
      {
        path: 'admin/reasons',
        element: (
          <AuthPage
            element={<Reasons />}
            permissions={[
              AccessElements.ReasonCreate,
              AccessElements.ReasonSearchGeneral
            ]}
          />
        )
      },
      {
        path: 'massiveLoad/ExternalFile',
        element: (
          <AuthPage
            element={<MassiveLoadExternalFile />}
            permissions={[AccessElements.TicketSaveMultiTicket]}
          />
        )
      },
      {
        path: 'admin/frecuentlyFailure',
        element: (
          <AuthPage
            element={<FrecuentlyFailure />}
            permissions={[
              AccessElements.FrequentFailureSearchGeneral,
              AccessElements.FrequentFailureCreate
            ]}
          />
        )
      },
      {
        path: 'ubications',
        element: (
          <AuthPage
            element={<Ubications />}
            permissions={[
              AccessElements.LocationSearch,
              AccessElements.LocationCreate
            ]}
          />
        )
      },
      {
        path: 'ubications/map',
        element: (
          <AuthPage
            element={<UbicationsMap />}
            permissions={[AccessElements.LocationGlobalMap]}
          />
        )
      },
      {
        path: 'ubications/massiveLoadUbications',
        element: (
          <AuthPage
            element={<MassiveLoadUbications />}
            permissions={[AccessElements.LocationBulkLoad]}
          />
        )
      },
      {
        path: 'ubications/massiveDeactivate',
        element: (
          <AuthPage
            element={<MassiveDeactivateUbications />}
            permissions={[AccessElements.LocationBulkDisable]}
          />
        )
      },
      {
        path: 'ubications/WorkForce',
        element: (
          <AuthPage
            element={<UbicationsWorkForce />}
            permissions={[AccessElements.LocationTaskForceGeolocation]}
          />
        )
      },
      // {
      //   path: 'admin/Escalations',
      //   element: (
      //     <AuthPage
      //       element={<Escalations />}
      //       permissions={[
      //         AccessElements.EscalationCreate,
      //         AccessElements.EscalationDelete,
      //         AccessElements.EscalationSearch,
      //         AccessElements.EscalationUpdate,
      //         AccessElements.EscalationGet,
      //         AccessElements.EscalationTypeGet
      //       ]}
      //     />
      //   )
      // },
      {
        path: 'admin/CreateAndConfigUser',
        element: (
          <AuthPage
            element={<UserConfig />}
            permissions={[
              AccessElements.UsersSearchUser,
              AccessElements.UsersCreateUser,
              AccessElements.UsersUpdateUser,
              AccessElements.UsersDeleteUser,
              AccessElements.UsersResetPassword,
              AccessElements.UsersChangePermissions,
              AccessElements.UsersWarehousePermissions,
              AccessElements.UsersClientPermissions,
              AccessElements.UsersTaskforcePermissions,
              AccessElements.UsersServiceTypePermissions,
              AccessElements.UsersCommentTypePermissions,
              AccessElements.UsersOperationsPermissions,
              AccessElements.UsersExportLog
            ]}
          />
        )
      },
      {
        path: 'admin/permissionsAudit',
        element: (
          <AuthPage
            element={<PermissionsAudit />}
            permissions={[AccessElements.UsersViewTrackingLog]}
          />
        )
      },
      {
        path: 'admin/kpis',
        element: (
          <AuthPage
            element={<KPIs />}
            permissions={[AccessElements.KPIManagement]}
          />
        )
      },
      {
        path: 'admin/formsManagement',
        element: (
          <AuthPage
            element={<FormsManagement />}
            permissions={[AccessElements.FormsFormManagement]}
          />
        )
      },
      {
        path: 'admin/AccessLogbook',
        element: (
          <AuthPage
            element={<AccessLogbook />}
            permissions={[AccessElements.UsersExportLog]}
          />
        )
      },
      {
        path: 'admin/groups',
        element: (
          <AuthPage
            element={<Groups />}
            permissions={[AccessElements.UsersChangePermissionsGroup]}
          />
        )
      },
      {
        path: 'admin/UnitsMeasurement',
        element: (
          <AuthPage
            element={<UnitsMeasurementCatalogue />}
            permissions={[
              AccessElements.UnitsMeasurementCreate,
              AccessElements.UnitsMeasurementSearchGeneral
            ]}
          />
        )
      },
      {
        path: 'admin/WarehouseType',
        element: (
          <AuthPage
            element={<WarehouseType />}
            permissions={[
              AccessElements.WarehouseTypeSearchGeneral,
              AccessElements.WarehouseTypeCreate
            ]}
          />
        )
      },
      {
        path: 'admin/clientsManagment',
        element: (
          <AuthPage
            element={<ClientsManagment />}
            permissions={[
              AccessElements.ClientSearchGeneral,
              AccessElements.ClientCreate,
              AccessElements.ClientUpdate,
              AccessElements.ClientDelete,
              AccessElements.ClientAddState,
              AccessElements.ClientEnableService,
              AccessElements.FlowTypeAdd,
              AccessElements.FlowTypeDelete,
              AccessElements.FlowTypeSearch,
              AccessElements.FlowTypeUpdate,
              AccessElements.ClientCreateODT,
              AccessElements.ClientUpdateODT,
              AccessElements.ClientDeleteODT,
              AccessElements.ClientSearchExportODT,
              AccessElements.ServiceCreate,
              AccessElements.ServiceSearchGeneral,
              AccessElements.ServiceUpdate,
              AccessElements.ServiceDelete,
              AccessElements.ServiceConfiguration
            ]}
          />
        )
      },
      {
        path: 'admin/ubitacionPriority',
        element: (
          <AuthPage
            element={<UbicationPriority />}
            permissions={[
              AccessElements.PrioritySearchGeneral,
              AccessElements.PriorityCreate
            ]}
          />
        )
      },
      {
        path: 'admin/serviceType',
        element: (
          <AuthPage
            element={<ServiceType />}
            permissions={[
              AccessElements.ServiceTypeSearchGeneral,
              AccessElements.ServiceTypeCreate
            ]}
          />
        )
      },
      {
        path: 'admin/brand',
        element: (
          <AuthPage
            element={<Brand />}
            permissions={[
              AccessElements.BrandSearchGeneral,
              AccessElements.BrandCreate
            ]}
          />
        )
      },
      {
        path: 'admin/calendar',
        element: (
          <AuthPage
            element={<Calendar />}
            permissions={[AccessElements.CatalogsHolidays]}
          />
        )
      },
      {
        path: 'admin/reportCatalogue',
        element: (
          <AuthPage
            element={<ReportsCatalogue />}
            permissions={[
              AccessElements.DashboardCreate,
              AccessElements.DashboardSearchGeneral
            ]}
          />
        )
      },
      {
        path: 'admin/equipmentType',
        element: (
          <AuthPage
            element={<EquipmentTypeCatalog />}
            permissions={[
              AccessElements.EquipmentTypeCreate,
              AccessElements.EquipmentTypeSearch
            ]}
          />
        )
      },
      {
        path: 'admin/postalCodes',
        element: (
          <AuthPage
            element={<PostalCodesCatalogue />}
            permissions={[
              AccessElements.CatalogsAddCP,
              AccessElements.CatalogsSearchCP
            ]}
          />
        )
      },
      {
        path: 'admin/model',
        element: (
          <AuthPage
            element={<Model />}
            permissions={[
              AccessElements.ProductModelSearchGeneral,
              AccessElements.ProductModelCreate
            ]}
          />
        )
      },
      {
        path: 'admin/directions',
        element: <AuthPage element={<Directions />} />
      },
      {
        path: 'admin/area',
        element: (
          <AuthPage
            element={<AreaCatalogue />}
            permissions={[
              AccessElements.WorkingAreaSearchGeneral,
              AccessElements.WorkingAreaCreate
            ]}
          />
        )
      },
      {
        path: 'admin/clasificationTicket',
        element: <AuthPage element={<ClasificationTicket />} permissions={[]} />
      },
      {
        path: 'admin/regions',
        element: (
          <AuthPage
            element={<RegionCatalogue />}
            permissions={[
              AccessElements.CatalogsSearchRegion,
              AccessElements.CatalogsAddRegion
            ]}
          />
        )
      },
      {
        path: 'admin/states',
        element: (
          <AuthPage
            element={<StateCatalogue />}
            permissions={[
              AccessElements.CatalogsSearchState,
              AccessElements.CatalogsAddState
            ]}
          />
        )
      },
      {
        path: 'admin/towerModel',
        element: (
          <AuthPage
            element={<TowerModel />}
            permissions={[
              AccessElements.TowerModelCreate,
              AccessElements.CatalogSearchTowerModel
            ]}
          />
        )
      },
      {
        path: 'admin/towerType',
        element: (
          <AuthPage
            element={<TowerType />}
            permissions={[
              AccessElements.TowerTypeCreate,
              AccessElements.CatalogSearchTowerType
            ]}
          />
        )
      },
      {
        path: 'admin/towerProperty',
        element: (
          <AuthPage
            element={<TowerProperty />}
            permissions={[
              AccessElements.TowerPropertyCreate,
              AccessElements.CatalogSearchTowerProperty
            ]}
          />
        )
      },
      {
        path: 'admin/invoiceTAG',
        element: (
          <AuthPage
            element={<InvoiceTAGCatalogue />}
            permissions={[
              AccessElements.TagCodeGeneralSearch,
              AccessElements.TagCodeCreate,
              AccessElements.TagCodeMassiveCreate
            ]}
          />
        )
      },
      {
        path: 'admin/invoiceFuel',
        element: (
          <AuthPage
            element={<InvoiceFuelCatalogue />}
            permissions={[
              AccessElements.FuelCodeCreate,
              AccessElements.FuelCodeBulkCreate,
              AccessElements.FuelCodeSearchGeneral
            ]}
          />
        )
      },
      {
        path: 'admin/expenseCode',
        element: (
          <AuthPage
            element={<InvoiceExpenseCodes />}
            permissions={[
              AccessElements.ExpenseCodeCreate,
              AccessElements.ExpenseCodeBulkCreate,
              AccessElements.ExpenseCodeSearchGeneral
            ]}
          />
        )
      },
      {
        path: 'admin/commentTypes',
        element: (
          <AuthPage
            element={<CommentTypes />}
            permissions={[
              AccessElements.TicketCommentTypeSearchGeneral,
              AccessElements.TicketCommentTypeCreate
            ]}
          />
        )
      },
      {
        path: 'admin/profiles',
        element: (
          <AuthPage
            element={<UserProfile />}
            permissions={[
              AccessElements.UsersSearchProfile,
              AccessElements.UsersCreateProfile
            ]}
          />
        )
      },
      {
        path: 'admin/projects',
        element: (
          <AuthPage
            element={<ProjectsCatalogue />}
            permissions={[
              AccessElements.ProjectSearch,
              AccessElements.ProjectAdd
            ]}
          />
        )
      },
      {
        path: 'admin/currency',
        element: (
          <AuthPage
            element={<CurrencyCatalogue />}
            permissions={[
              AccessElements.CurrencySearchGeneral,
              AccessElements.CurrencyCreate
            ]}
          />
        )
      },
      {
        path: 'admin/audit',
        element: (
          <AuthPage
            element={<Audit />}
            permissions={[
              AccessElements.AuditCreate,
              AccessElements.AuditCreateReport,
              AccessElements.AuditReportSearchGeneral,
              AccessElements.AuditSearchGeneral
            ]}
          />
        )
      },
      {
        path: 'admin/catalogAudit',
        element: (
          <AuthPage
            element={<AuditCatalog />}
            permissions={[
              AccessElements.AuditTypeCreate,
              AccessElements.AuditTypeSearchGeneral
            ]}
          />
        )
      },
      {
        path: 'admin/status',
        element: (
          <AuthPage
            element={<StatusCatalogue />}
            permissions={[
              AccessElements.TicketStatusAdd,
              AccessElements.TicketStatusSearch
            ]}
          />
        )
      },
      {
        path: 'admin/flowBoard',
        element: (
          <AuthPage
            element={<FlowDashboard />}
            permissions={[AccessElements.CatalogShowTicketFlow]}
          />
        )
      },
      {
        path: 'admin/workFlowType',
        element: (
          <AuthPage
            element={<WorkFlowType />}
            permissions={[
              AccessElements.FlowTypeSearch,
              AccessElements.FlowTypeAdd
            ]}
          />
        )
      },
      {
        path: 'admin/componentCondition',
        element: <AuthPage element={<ComponentCondition />} />
      },
      {
        path: 'admin/IRFlow',
        element: <AuthPage element={<IncidetAndRequirmentFlow />} />
      },
      {
        path: 'admin/ResponsibleType',
        element: (
          <AuthPage
            element={<ResponsibleType />}
            permissions={[
              AccessElements.ResponsibleSearchGeneral,
              AccessElements.ResponsibleCreateType
            ]}
          />
        )
      },
      {
        path: 'admin/CABManagement',
        element: (
          <AuthPage
            element={<CABManagement />}
            permissions={[
              AccessElements.ChangeAdvisoryBoardCreate,
              AccessElements.ChangeAdvisoryBoardSearch
            ]}
          />
        )
      },
      {
        path: 'admin/Indicators',
        element: (
          <AuthPage
            element={<Indicators />}
            permissions={[AccessElements.ServiceSearchIndicator]}
          />
        )
      },
      {
        path: 'configurationComponent/Map',
        element: (
          <AuthPage
            element={<ConfigComponentsMap />}
            permissions={[AccessElements.ProductMap]}
          />
        )
      },
      {
        path: '/Supplies',
        element: (
          <AuthPage
            element={<Supplies />}
            permissions={[
              AccessElements.SupplyCreate,
              AccessElements.SupplySearchGeneral
            ]}
          />
        )
      },
      {
        path: '/StockSupplies',
        element: (
          <AuthPage
            element={<StockSupplies />}
            permissions={[
              AccessElements.SuppliesAdd,
              AccessElements.SuppliesSearch
            ]}
          />
        )
      },
      // GESTION ACTIVO TI - ALMACEN
      {
        path: `/warehousesComponentInventory`,
        element: (
          <AuthPage
            element={<WarehousesComponentInventory />}
            permissions={[AccessElements.AssetsWarehouseProductsSearchExport]}
          />
        )
      },
      {
        path: `/warehousesSupplyInventory`,
        element: (
          <AuthPage
            element={<WarehousesSupplyInventory />}
            permissions={[AccessElements.AssetsSuppliesSearchExport]}
          />
        )
      },
      {
        path: `/kitsWarehouse`,
        element: (
          <AuthPage
            element={<KitsInventoryWarehouse />}
            permissions={[AccessElements.AssetsKitsSearchExport]}
          />
        )
      },
      // GESTION ACTIVO TI - LABORATORIO
      {
        path: `/laboratoryComponentInventory`,
        element: (
          <AuthPage
            element={<LaboratoryComponentInventory />}
            permissions={[AccessElements.AssetsLaboratoryProductsSearchExport]}
          />
        )
      },
      {
        path: `/laboratory/bulkReception`,
        element: (
          <AuthPage
            element={<LaboratoryBulkCIIntake />}
            permissions={[AccessElements.LaboratoryAceptAndRejectProducts]}
          />
        )
      },
      // GESTION ACTIVO TI - INGENIERO
      {
        path: 'assetManagementIT/AuditReport',
        element: (
          <AuthPage
            element={<AuditReport />}
            permissions={[AccessElements.AuditReportSearchGeneral]}
          />
        )
      },
      // GESTION ACTIVO TI - INGENIERO
      {
        path: 'assetManagementIT/workForce/configComponent/find',
        element: (
          <AuthPage
            element={<WorkForceConfigComponentsInventory />}
            permissions={[AccessElements.AssetsWorkForceProductsSearchExport]}
          />
        )
      },
      {
        path: 'assetManagementIT/workForce/supplies/find',
        element: (
          <AuthPage
            element={<WorkForceSuppliesInventory />}
            permissions={[AccessElements.AssetsWorkForceSuppliesSearchExport]}
          />
        )
      },
      {
        path: 'assetManagementIT/workForce/kits/find',
        element: (
          <AuthPage
            element={<WorkForceKitCIInventory />}
            permissions={[AccessElements.AssetsWarehouseKitsSearchExport]}
          />
        )
      },
      {
        path: 'admin/interventionCodes',
        element: (
          <AuthPage
            element={<InterventionCodes />}
            permissions={[
              AccessElements.InterventionCodeSearch,
              AccessElements.InterventionCodeAdd
            ]}
          />
        )
      },
      {
        path: '/kits',
        element: (
          <AuthPage
            element={<KitsCI />}
            permissions={[
              AccessElements.KitCreate,
              AccessElements.KitDelete,
              AccessElements.KitComponentsUpdate,
              AccessElements.KitQuery,
              AccessElements.KitUpdate,
              AccessElements.KitLog
            ]}
          />
        )
      },
      {
        path: 'informativeModule/workForce',
        element: (
          <AuthPage element={<InformativeModuleWorkForce />} permissions={[]} />
        )
      },
      {
        path: 'informativeModule/clients',
        element: (
          <AuthPage element={<InformativeModuleClients />} permissions={[]} />
        )
      },
      {
        path: 'notifications',
        element: (
          <AuthPage
            element={<NotificationsPage />}
            permissions={[AccessElements.NotificationSearch]}
          />
        )
      },
      {
        path: '/help/FAQs',
        element: <AuthPage element={<FAQs />} permissions={[]} />
      },
      {
        path: '/reports',
        element: <AuthPage element={<Reports />} permissions={[]} />
      },
      {
        path: '/incidentReports/open',
        element: (
          <AuthPage
            element={<IncidentOpenReport />}
            permissions={[AccessElements.DashboardOpenIncidents]}
          />
        )
      },
      {
        path: '/incidentReports/historical',
        element: (
          <AuthPage
            element={<IncidentHistoricalReport />}
            permissions={[AccessElements.DashboardHistoricalIncidents]}
          />
        )
      },
      {
        path: 'exportTicketsReport',
        element: (
          <AuthPage
            element={<ExportTicketReports />}
            permissions={[AccessElements.TicketReportGeneralSearch]}
          />
        )
      },
      {
        path: 'expenseReport',
        element: <AuthPage element={<ExpenseReports />} permissions={[]} />
      },
      {
        path: 'recidivists',
        element: <AuthPage element={<RecidivistsReports />} permissions={[]} />
      },
      {
        path: 'mayorIncident',
        element: <AuthPage element={<MayorIncident />} permissions={[]} />
      },
      {
        path: '/KMDB',
        element: (
          <AuthPage
            element={<KnowledgeManagementDatabase />}
            permissions={[]}
          />
        )
      },
      {
        path: 'remoteWork',
        element: (
          <AuthPage
            element={<RemoteWork />}
            permissions={[AccessElements.DashboardShowReportRemoteWork]}
          />
        )
      },
      {
        path: '/surveys/new',
        element: (
          <AuthPage
            element={<Surveys />}
            permissions={[AccessElements.SurveyManagement]}
          />
        )
      },
      {
        path: '/admin/notification',
        element: (
          <AuthPage
            element={<NotificationCatalogue />}
            permissions={[AccessElements.CatalogShowNotificationCatalog]}
          />
        )
      },
      {
        path: '/admin/notification-configurator',
        element: (
          <AuthPage
            element={<NotificationConfigurator />}
            permissions={[AccessElements.NotificationConfigurator]}
          />
        )
      },
      {
        path: '/admin/notification-configurator/create',
        element: (
          <AuthPage
            element={<NotificationConfiguratorForm />}
            permissions={[AccessElements.NotificationConfigurator]}
          />
        )
      },
      {
        path: '/admin/notification-configurator/edit/:id',
        element: (
          <AuthPage
            element={<NotificationConfiguratorForm />}
            permissions={[AccessElements.NotificationConfigurator]}
          />
        )
      },
      {
        path: '/admin/serviceClass',
        element: (
          <AuthPage
            element={<ServiceClassCatalogue />}
            permissions={[
              AccessElements.ServiceClassCreate,
              AccessElements.ServiceClassSearch
            ]}
          />
        )
      },
      {
        path: '/admin/HierarchyLevels',
        element: (
          <AuthPage
            element={<HierarchyLevels />}
            permissions={[AccessElements.HierarchicalLevelManagement]}
          />
        )
      },
      {
        path: '/admin/HierarchicalStructure',
        element: (
          <AuthPage
            element={<HierarchicalStructure />}
            permissions={[AccessElements.HierarchicalLevelGlobalView]}
          />
        )
      },
      {
        path: '/admin/massiveHierarchicalStructureUpdate',
        element: (
          <AuthPage
            element={<MassiveHierarchicalStructureUpdate />}
            permissions={[AccessElements.HierarchicalLevelBulkUpdate]}
          />
        )
      },
      {
        path: '/surveys/search',
        element: (
          <AuthPage
            element={<SurveySearch />}
            permissions={[AccessElements.SurveySearch]}
          />
        )
      },
      {
        path: '/surveys/report',
        element: (
          <AuthPage
            element={<SurveyReport />}
            permissions={[AccessElements.SurveyReport]}
          />
        )
      },
      {
        path: '/changeTypes',
        element: (
          <AuthPage
            element={<ChangeTypes />}
            permissions={[
              AccessElements.ChangeTypeCreate,
              AccessElements.ChangeTypeDelete,
              AccessElements.ChangeTypeUpdate,
              AccessElements.ChangeTypeSearch
            ]}
          />
        )
      },
      {
        path: '/statesOfChange',
        element: (
          <AuthPage
            element={<StatesOfChange />}
            permissions={[AccessElements.ChangeStageSearchGeneral]}
          />
        )
      },
      {
        path: '/admin/stagesAndStatuses',
        element: (
          <AuthPage
            element={<StatesAndStagesOfChanges />}
            permissions={[AccessElements.ChangeStageStatusSearchGeneral]}
          />
        )
      },
      {
        path: '/admin/statusesOfTasks',
        element: (
          <AuthPage
            element={<StatesOfTasks />}
            permissions={[AccessElements.TaskStatusSearchGeneral]}
          />
        )
      },
      {
        path: '/admin/tasktemplate',
        element: (
          <AuthPage
            element={<TasksTemplate />}
            permissions={[AccessElements.TaskTemplateSearch]}
          />
        )
      },
      {
        path: '/admin/flowOfChanges',
        element: (
          <AuthPage
            element={<FlowOfChanges />}
            permissions={[
              AccessElements.ChangeFlowCreate,
              AccessElements.ChangeFlowDelete,
              AccessElements.ChangeFlowSearch,
              AccessElements.ChangeFlowUpdate
            ]}
          />
        )
      },
      {
        path: '/admin/problem/statusFlows',
        element: (
          <AuthPage
            element={<StatusFlow />}
            permissions={[AccessElements.ProblemFlowSearch]}
          />
        )
      },
      {
        path: '/admin/problem/causes-catalog',
        element: (
          <AuthPage element={<ProblemCausesCatalog />} permissions={[]} />
        )
      },
      {
        path: '/admin/problem/expert-committees-catalog',
        element: (
          <AuthPage element={<ExpertCommitteesCatalog />} permissions={[]} />
        )
      },
      {
        path: '/admin/problem/status',
        element: (
          <AuthPage
            element={<Status />}
            permissions={[AccessElements.ProblemStatusSearch]}
          />
        )
      },
      {
        path: '/admin/release/status',
        element: (
          <AuthPage
            element={<StatusRelease />}
            permissions={[AccessElements.ReleaseStatusSearch]}
          />
        )
      },
      {
        path: '/admin/release/statusFlows',
        element: (
          <AuthPage
            element={<StatusFlowRelease />}
            permissions={[AccessElements.ReleaseFlowSearch]}
          />
        )
      },
      // TODO: Descomentar cuando se haga template de lanzamientos
      // {
      //   path: '/admin/release/templates',
      //   element: (
      //     <AuthPage
      //       element={<ReleaseTemplates />}
      //       //  permissions={[AccessElements.ReleaseStatusSearch]}
      //     />
      //   )
      // },
      {
        path: '/problem/search',
        element: (
          <AuthPage
            element={<ProblemSearch />}
            permissions={[AccessElements.ProblemSearch]}
          />
        )
      },
      {
        path: '/admin/problem/automaticProblemDetection',
        element: (
          <AuthPage element={<AutomaticProblemDetection />} permissions={[]} />
        )
      },
      {
        path: '/problem/create',
        element: (
          <AuthPage
            element={<NewProblem />}
            permissions={[AccessElements.ProblemCreate]}
          />
        )
      },
      {
        path: '/problem/update/:problemId',
        element: (
          <AuthPage
            element={<RedirectProblemEditToDetail />}
            permissions={[AccessElements.ProblemUpdate]}
          />
        )
      },
      {
        path: '/problem/detail/:problemId',
        element: (
          <AuthPage
            element={<ProblemDetails />}
            permissions={[AccessElements.ProblemSearch]}
          />
        )
      },
      {
        path: '/admin/catalogs/problem-templates',
        element: (
          <AuthPage
            element={<ProblemTemplates />}
            permissions={[AccessElements.ProblemTemplateSearch]}
          />
        )
      },
      {
        path: '/admin/catalogs/problem-templates/create',
        element: (
          <AuthPage
            element={<CreateEditProblemTemplate />}
            permissions={[AccessElements.ProblemTemplateCreate]}
          />
        )
      },
      {
        path: '/admin/catalogs/problem-templates/edit/:id',
        element: (
          <AuthPage
            element={<CreateEditProblemTemplate />}
            permissions={[AccessElements.ProblemTemplateUpdate]}
          />
        )
      }
    ]
  },
  {
    path: '/forbidden',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ForbiddenPage />
      </Suspense>
    )
  },
  {
    path: '*',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <NotFoundPage />
      </Suspense>
    )
  }
];

export default routes;
